import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Moment } from "moment";
import useTablePagination from "../../../hooks/useTablePagination";
import { RootState } from "../../../appRedux/store";
import useValue from "../../../hooks/useValue";
import { CustomNotification } from "../../../util/CustomNotification";
import * as reportServices from "../../../services/reportsService";
import { IotHubLogsInterface } from "../../../shared/interface/Report/iotHubLogs.interface";
import { SyncDirectionConstants } from "../../../constants/DatabaseConstants/ParkingConstants";
import { DefaultPagination } from "../../../constants/generalConstants";
import { ParamsObject } from "../../../shared/interface/parking.interface";
import { General } from "../../../util/General";

export const useIotHubLogs = () => {
  /* STATE */
  const [iotHubLogList, setIotHubLogList] = useState<IotHubLogsInterface[]>([]);
  const [searchedText, setSearchedText] = useState("");
  const [selectedMode, setSelectedMode] = useState<string>(SyncDirectionConstants.CLOUD_TO_DEVICE);
  const [dateRange, setDateRange] = useState<{
    fromDate: string | null;
    toDate: string | null;
  }>({
    fromDate: null,
    toDate: null
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<IotHubLogsInterface | null>(null);

  const { masterFilters } = useSelector((state: RootState) => state.common);

  /* HOOK */
  const { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination } = useTablePagination(0);
  const { value: loading, handleEnableValue: startLoading, handleDisableValue: stopLoading } = useValue(false);

  /* HANDLERS */
  const handleChangeDatePicker = (dates: [Moment | null, Moment | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      setDateRange({ fromDate: null, toDate: null });
      return;
    }

    const fromDate = dates[0].format("YYYY-MM-DD HH:mm");
    const toDate = dates[1].format("YYYY-MM-DD HH:mm");

    try {
      /* Validate date range using the function */
      General.validateDateRange({ fromDate, toDate }, "YYYY-MM-DD HH:mm");
      setDateRange({ fromDate, toDate });
    } catch (error) {
      CustomNotification.showErrorMessage(error);
      return false;
    }
  };

  const handleChangeSearch = (value: string) => {
    setSearchedText(value.trimStart());
  };

  const handleChangeStatusFilter = (value: string) => {
    handleResetPagination();
    setSelectedMode(value);
  };

  const fetchIotHubLogsList = async () => {
    try {
      startLoading();

      const paramsObject: ParamsObject = {
        syncDirection: selectedMode,
        page: pagination?.current || DefaultPagination.PAGE,
        pageSize: pagination?.pageSize || DefaultPagination.PAGE_SIZE,
        customerId: masterFilters?.customerId,
        locationId: masterFilters?.locationId,
        fromDate: dateRange?.fromDate,
        toDate: dateRange?.toDate
      };

      const response = await reportServices.getIotHubLogs(paramsObject);

      if (response?.data) {
        const formatted = response.data?.rows?.map((item, index) => ({
          ...item,
          sr_no: index + 1
        }));

        handleChangeTotalSize(response?.data?.count || 0);
        setIotHubLogList(formatted);
      }
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      stopLoading();
    }
  };

  const handleOnClose = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
  };

  const handleRefreshList = () => fetchIotHubLogsList();

  const handleModalOpen = (record: IotHubLogsInterface) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setSelectedRecord(null);
    setIsModalVisible(false);
  };

  const handleNextRecord = () => {
    if (!selectedRecord || !iotHubLogList) return;
    const currentIndex = iotHubLogList.findIndex((r) => r.sr_no === selectedRecord.sr_no);
    const nextRecord = iotHubLogList[currentIndex + 1];
    if (nextRecord) setSelectedRecord(nextRecord);
  };

  const handlePreviousRecord = () => {
    if (!selectedRecord || !iotHubLogList) return;
    const currentIndex = iotHubLogList.findIndex((r) => r.sr_no === selectedRecord.sr_no);
    const previousRecord = iotHubLogList[currentIndex - 1];
    if (previousRecord) setSelectedRecord(previousRecord);
  };

  /* EFFECT */
  useEffect(() => {
    if (masterFilters?.customerId && masterFilters?.locationId && pagination?.current && pagination?.pageSize) {
      fetchIotHubLogsList();
    }
  }, [pagination?.current, pagination?.pageSize, masterFilters?.customerId, masterFilters?.locationId, selectedMode, dateRange?.fromDate && dateRange?.toDate]);

  return {
    iotHubLogList,
    selectedMode,
    loading,
    pagination,
    handleChangePagination,
    handleRefreshList,
    isModalVisible,
    selectedRecord,
    handleChangeSearch,
    searchedText,
    handleOnClose,
    handleModalOpen,
    handleChangeStatusFilter,
    handleChangeDatePicker,
    dateRange,
    handleModalClose,
    handleNextRecord,
    handlePreviousRecord
  };
};
